import { AsyncPipe, NgIf, NgTemplateOutlet } from '@angular/common';
import { Component, DestroyRef, Input, OnChanges, OnInit, Signal, SimpleChanges, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { QuoteProduct } from '@lx/quote-tool/shared/types';
import { Transfer, TransferServiceType, TransferServiceTypeEnum } from '@lx/shared/data-access/products';
import { Amount } from '@lx/shared/data-access/types';
import { LoadingService } from '@lx/shared/ui/loading-spinner';
import { isDeepEqual } from '@lx/shared/utils';
import { BehaviorSubject, Observable } from 'rxjs';
import { GoogleAnalyticsEventEnum } from '../../google-analytics';
import { QTLiteStoreSignalsAdapterService } from '../../services/qt-lite-store-signals-adapter.service';
import { QuoteOutputTransfer, QuoteStore, QuoteToolLiteStore } from '../../store';
import { Flows, Flow } from '@lx/quote-tool/lite/shared/types';
import { CardContainerComponent } from '@lx/quote-tool/lite/shared/ui/components';
import { SidePanelComponent } from '@lx/quote-tool/lite/shared/ui/components';
import { TransfersSelectorComponent } from '../transfers-selector/transfers-selector.component';
import { OperationKey } from '../../constants';
import { SidePanelService } from '@lx/quote-tool/lite/shared/services';

@Component({
  standalone: true,
  selector: 'lx-transfers',
  templateUrl: './transfers.component.html',
  styleUrls: ['./transfers.component.scss'],
  imports: [CardContainerComponent, NgIf, SidePanelComponent, TransfersSelectorComponent, AsyncPipe, NgTemplateOutlet],
})
export class TransfersComponent implements OnInit, OnChanges {
  @Input() groupedTransfers: Record<TransferServiceType, Transfer[]> | undefined;

  dialogVisible = false;
  selectedTransferRows = [] as number[];

  transferRows$: Observable<number[]> | undefined;
  price: Amount[] | undefined;
  endPrice: Amount[] | undefined;
  discount: string | undefined;
  activeFlow$: Observable<Flow> | undefined;
  currency: Signal<string | undefined>;

  transfers$: Observable<QuoteOutputTransfer[]> | undefined;

  // Arrival transfer name
  arrival: string | undefined;

  // Departure transfer name
  departure: string | undefined;

  dataLayerEventName$: Observable<GoogleAnalyticsEventEnum> | undefined;

  protected loading: Signal<boolean | undefined>;
  protected readonly Flows = Flows;

  private dataLayerEventName = new BehaviorSubject<GoogleAnalyticsEventEnum>(undefined);

  private readonly qtLiteStore = inject(QuoteToolLiteStore);
  private readonly qtLiteStoreSignalsAdapterService = inject(QTLiteStoreSignalsAdapterService);
  private readonly quoteStore = inject(QuoteStore);
  private readonly sidePanelService = inject(SidePanelService);
  private readonly loadingService = inject(LoadingService);
  private readonly destroyRef = inject(DestroyRef);

  ngOnInit(): void {
    this.transfers$ = this.quoteStore.transfers$;
    this.transferRows$ = this.qtLiteStore.selectedTransferRows$;
    this.activeFlow$ = this.qtLiteStore.activeFlow$;

    this.quoteStore.transfersWithTotalPrices$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(({ transfers, totalPrices }) => {
      this.setSelectedTransfersDescription(transfers);
      if (!transfers?.length) {
        // If there are no transfers, set the default prices
        this.setDefaultTransferPrices();
        return;
      }

      const { endPrice, price, discount } = totalPrices;
      this.endPrice = endPrice;
      this.price = price;
      this.discount = discount;
    });

    this.dataLayerEventName$ = this.dataLayerEventName.asObservable();

    this.qtLiteStore.selectedTransferRows$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((selectedTransferRows) => {
      if (selectedTransferRows?.length) {
        this.dataLayerEventName.next(GoogleAnalyticsEventEnum.QuoteTransferEdit);
      } else {
        this.dataLayerEventName.next(GoogleAnalyticsEventEnum.QuoteTransferAdd);
      }
    });

    this.currency = this.qtLiteStoreSignalsAdapterService.selectedCurrency;
    this.loading = this.loadingService.areAllOperationsLoading([OperationKey.prices, OperationKey.extras]);
  }

  ngOnChanges({ groupedTransfers }: SimpleChanges): void {
    // If it is the first time the component is displayed or the input transfers have changed, before any calculation,
    // or taking information from the store, let's set the endPrice to the price of the cheapest transfer
    if (!isDeepEqual(groupedTransfers?.currentValue, groupedTransfers?.previousValue) || groupedTransfers?.isFirstChange()) {
      let transferToTakeEndPrice = undefined;
      const groupedTransfersCurrentValue = groupedTransfers?.currentValue;
      if (groupedTransfersCurrentValue?.ARRIVAL) {
        transferToTakeEndPrice = groupedTransfers.currentValue.ARRIVAL;
      } else if (groupedTransfersCurrentValue?.DEPARTURE) {
        transferToTakeEndPrice = groupedTransfersCurrentValue?.DEPARTURE;
      }

      if (transferToTakeEndPrice) {
        this.endPrice = transferToTakeEndPrice[0].multiCurrencyPrice;
        this.price = transferToTakeEndPrice[0].multiCurrencyPrice;
      }
    }
  }

  setSelectedTransferRows(transferRows: number[]) {
    this.selectedTransferRows = transferRows;
  }

  saveTransferRows() {
    this.hideDialog(() => this.qtLiteStore.updateState({ selectedTransferRows: this.selectedTransferRows }));
  }

  openDialog() {
    this.dialogVisible = true;
  }

  hideDialog(callback?: () => void) {
    this.sidePanelService.hidePanel(() => {
      this.dialogVisible = false;
      if (callback) {
        callback();
      }
    });
  }

  /**
   * Set the default prices: they will be taken from the price of the cheapest transfer.
   */
  private setDefaultTransferPrices() {
    if (!this.groupedTransfers?.ARRIVAL?.length) {
      this.endPrice = [];
      this.price = [];
    }

    if (this.groupedTransfers?.ARRIVAL?.length) {
      this.endPrice = this.groupedTransfers[TransferServiceTypeEnum.ARRIVAL][0].multiCurrencyPrice;
      this.price = this.groupedTransfers[TransferServiceTypeEnum.ARRIVAL][0].multiCurrencyPrice;
    }
  }

  private setSelectedTransfersDescription(quotedTransfers: QuoteProduct[]) {
    if (quotedTransfers?.length) {
      this.arrival = quotedTransfers.find((transfer) => transfer.transferServiceType === TransferServiceTypeEnum.ARRIVAL)?.name;
      this.departure = quotedTransfers.find((transfer) => transfer.transferServiceType === TransferServiceTypeEnum.DEPARTURE)?.name;
    } else {
      this.arrival = '';
      this.departure = '';
    }
  }
}
